// Installable app: service worker registration, the install prompt
// (beforeinstallprompt, Chromium only), in-app launches (launch_handler
// "focus-existing": a video shared from the YouTube app while this app is open
// arrives via window.launchQueue instead of reloading the page, so running
// summaries keep going) and "summary ready" notifications.
import { useEffect, useState } from 'react';
import type { PushKeyResponse, PushTarget } from '../../shared/api';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface LaunchParams {
  readonly targetURL?: string;
}

declare global {
  interface Window {
    launchQueue?: { setConsumer(consumer: (params: LaunchParams) => void): void };
  }
}

export function registerServiceWorker(): void {
  if (!('serviceWorker' in navigator) || !window.isSecureContext || import.meta.env.DEV) return;
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((error: unknown) => {
      console.warn('[pwa] service worker registration failed:', error);
    });
  });
}

export function isStandalone(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

// The event fires early, often before React mounts: capture it at module load.
let deferredPrompt: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    notify();
  });
  window.addEventListener('appinstalled', () => {
    deferredPrompt = null;
    notify();
  });
}

/** `canInstall`: the browser offers installation and the app is not installed yet. */
export function useInstallPrompt(): { canInstall: boolean; install: () => Promise<boolean> } {
  const [canInstall, setCanInstall] = useState(() => !!deferredPrompt && !isStandalone());
  useEffect(() => {
    const update = () => setCanInstall(!!deferredPrompt && !isStandalone());
    listeners.add(update);
    update();
    return () => {
      listeners.delete(update);
    };
  }, []);
  return {
    canInstall,
    install: async () => {
      const event = deferredPrompt;
      if (!event) return false;
      deferredPrompt = null;
      notify();
      await event.prompt();
      const { outcome } = await event.userChoice;
      return outcome === 'accepted';
    },
  };
}

// The address this page was loaded with (before the router normalises it).
const initialHref = typeof window !== 'undefined' ? window.location.href : '';

/**
 * Calls `handler` with the target URL of every launch into the already open
 * app (share target, start URL) and of every tapped notification. The launch
 * that loaded this page is skipped: the router reads it from location already.
 * Returns an unsubscribe function (launchQueue has no real unsubscribe; later
 * launches are ignored).
 */
export function onLaunch(handler: (url: URL) => void): () => void {
  let active = true;
  let first = true;
  window.launchQueue?.setConsumer((params) => {
    const skip = first;
    first = false;
    if (!active || !params.targetURL) return;
    const url = new URL(params.targetURL);
    if (skip && url.href === initialHref) return;
    handler(url);
  });
  const onMessage = (event: MessageEvent) => {
    const data = event.data as { type?: unknown; url?: unknown } | null;
    if (!active || data?.type !== 'open-url' || typeof data.url !== 'string') return;
    const url = new URL(data.url, window.location.origin);
    if (url.origin === window.location.origin) handler(url);
  };
  navigator.serviceWorker?.addEventListener('message', onMessage);
  return () => {
    active = false;
    navigator.serviceWorker?.removeEventListener('message', onMessage);
  };
}

// ---------------------------------------------------------------------------
// Notifications. Shown through the service worker: Chrome on Android does not
// support `new Notification()`, and the worker opens the summary on tap.

export type NotificationState = NotificationPermission | 'unsupported';

export function notificationState(): NotificationState {
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !window.isSecureContext) return 'unsupported';
  return Notification.permission;
}

/** The current permission; re-read when the app comes back to the foreground (it may have changed in the system settings). */
export function useNotificationState(): [NotificationState, () => void] {
  const [state, setState] = useState<NotificationState>(notificationState);
  useEffect(() => {
    const update = () => setState(notificationState());
    document.addEventListener('visibilitychange', update);
    window.addEventListener('focus', update);
    return () => {
      document.removeEventListener('visibilitychange', update);
      window.removeEventListener('focus', update);
    };
  }, []);
  return [state, () => setState(notificationState())];
}

/** Asks for permission (must be called from a user gesture); true when granted. */
export async function requestNotifications(): Promise<boolean> {
  if (notificationState() === 'unsupported') return false;
  try {
    return (await Notification.requestPermission()) === 'granted';
  } catch {
    return false;
  }
}

export interface AppNotification {
  title: string;
  body?: string;
  /** Same-origin path opened when the notification is tapped. */
  url: string;
  /** Replaces an earlier notification with the same tag (e.g. the video id). */
  tag?: string;
}

/** Shows a notification if permitted; resolves false when it could not be shown. */
export async function showNotification({ title, body, url, tag }: AppNotification): Promise<boolean> {
  if (notificationState() !== 'granted') return false;
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return false;
    // The server's push for the same event may have arrived first (sw.js checks the same way).
    if (tag && (await registration.getNotifications({ tag })).some((n) => n.title === title)) return true;
    await registration.showNotification(title, {
      ...(body ? { body } : {}),
      ...(tag ? { tag } : {}),
      icon: '/icon-192.png',
      data: { url },
    });
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// Web Push: while Android suspends the app, only the server can notify. The
// subscription is sent along with each summarize request (see PushTarget).

let pushKey: Promise<string | null> | null = null;

function loadPushKey(): Promise<string | null> {
  pushKey ??= fetch('/api/push/key', { credentials: 'same-origin' })
    .then(async (response) => (response.ok ? ((await response.json()) as PushKeyResponse).publicKey : null))
    .catch(() => {
      pushKey = null; // retry next time
      return null;
    });
  return pushKey;
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
  const base64 = (value + '='.repeat((4 - (value.length % 4)) % 4)).replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(new ArrayBuffer(binary.length));
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes;
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a || a.byteLength !== b.byteLength) return false;
  const view = new Uint8Array(a);
  return view.every((byte, i) => byte === b[i]);
}

async function subscribePush(): Promise<PushTarget | undefined> {
  if (notificationState() !== 'granted') return undefined;
  const registration = await navigator.serviceWorker.getRegistration();
  if (!registration?.pushManager) return undefined;
  const key = await loadPushKey();
  if (!key) return undefined;
  const serverKey = base64UrlToBytes(key);
  let subscription = await registration.pushManager.getSubscription();
  // Subscribed with another server key (keys rotated): the push service would refuse our pushes.
  if (subscription && !sameKey(subscription.options.applicationServerKey, serverKey)) {
    await subscription.unsubscribe().catch(() => false);
    subscription = null;
  }
  subscription ??= await registration.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: serverKey });
  const json = subscription.toJSON();
  if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) return undefined;
  return { endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } };
}

/**
 * This browser's push subscription (subscribing on first use) when
 * notifications are permitted; undefined otherwise or when it takes too long
 * (never delays a summary by more than a moment).
 */
export async function getPushTarget(timeoutMs = 3_000): Promise<PushTarget | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      subscribePush(),
      new Promise<undefined>((resolve) => {
        timer = setTimeout(() => resolve(undefined), timeoutMs);
      }),
    ]);
  } catch {
    return undefined;
  } finally {
    clearTimeout(timer);
  }
}
